import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { list_plans } from "services/apiSrv";


// Styled Components
const Section = styled.div`
    padding: 6rem 1.5rem;
    background-color: #ffffff;
    margin-top: 4px;
    min-height: 100vh;
    position: relative;
    isolation: isolate;
    border-radius: 12px;
    @media (min-width: 640px) {
        padding-top: 8rem;
        padding-bottom: 8rem;
    }
    @media (min-width: 1024px) {
        padding-left: 2rem;
        padding-right: 2rem;
    }
`;

const BackgroundGradient = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    top: -0.75rem;
    z-index: -10;
    will-change: transform;
    overflow: hidden;
    padding-left: 9rem;
    padding-right: 9rem;
    filter: blur(24px);
`;

const GradientShape = styled.div`
    margin-left: auto;
    margin-right: auto;
    aspect-ratio: 1155 / 678;
    width: 72.1875rem;
    background: linear-gradient(to top right, #ff80b5, #9089fc);
    opacity: 0.3;
    clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%);
`;

const Container = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    text-align: center;
`;

const PageHeader = styled.div`
    max-width: 1024px;
    margin: 0 auto;
    margin-bottom: 1.5rem;
`;

const PageTitle = styled.h2`
    font-size: 1rem;
    font-weight: 600;
    color: #4f46e5;
    line-height: 1.75;
    @media (min-width: 640px) {
        font-size: 1.5rem;
    }
`;

const PageSubtitle = styled.p`
    margin-top: 0.5rem;
    font-size: 2.25rem;
    font-weight: 600;
    color: #111827;
    line-height: 1;
    @media (min-width: 640px) {
        font-size: 3rem;
    }
`;

const Description = styled.p`
    margin-top: 1.5rem;
    max-width: 672px;
    margin-left: auto;
    margin-right: auto;
    font-size: 1.125rem;
    font-weight: 500;
    color: #4b5563;
    line-height: 1.625;
    @media (min-width: 640px) {
        font-size: 1.25rem;
        line-height: 2;
    }
`;

const PricingGrid = styled.div`
    margin-top: 4rem;
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    max-width: 448px;
    margin-left: auto;
    margin-right: auto;
    align-items: center;
    @media (min-width: 640px) {
        margin-top: 5rem;
        gap: 0;
    }
    @media (min-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
        max-width: 1024px;
    }
`;

const PricingCard = styled.div`
    background: rgba(255, 255, 255, 0.9);
    border-radius: 1.5rem;
    padding: 2.5rem;
    border: 1px solid rgba(17, 24, 39, 0.1);
    display: flex;
    flex-direction: column;
    height: 100%;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1), 0 15px 25px rgba(0, 0, 0, 0.15);
    }
    @media (min-width: 640px) {
        margin-left: 2rem;
        margin-right: 2rem;
        padding: 3rem;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }
    @media (min-width: 1024px) {
        margin-left: 0;
        margin-right: 0;
        border-top-right-radius: ${props => (props.isEnterprise ? '0' : '1.5rem')};
        border-bottom-left-radius: ${props => (props.isEnterprise ? '1.5rem' : '0')};
    }
`;

const PlanName = styled.h3`
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.5;
    letter-spacing: -0.025em;
`;

const PlanType = styled.span`
    display: inline-block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #ffffff;
    background-color: ${props => (props.planType === 'FREE' ? '#10b981' : '#4f46e5')};
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    margin-top: 0.5rem;
`;

const PlanPrice = styled.div`
    margin-top: 1.5rem;
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
`;

const PriceAmount = styled.span`
    font-size: 2.5rem;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.025em;
`;

const PricePeriod = styled.span`
    font-size: 1rem;
    font-weight: 500;
    color: #6b7280;
`;

const DiscountTag = styled.div`
    margin-top: 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #047857;
    background: #d1fae5;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    display: inline-block;
`;

const PlanDescription = styled.p`
    margin-top: 1.5rem;
    font-size: 1.125rem;
    font-weight: 400;
    color: #374151;
    line-height: 1.75;
    background: rgba(79, 70, 229, 0.05);
    padding: 1rem;
    border-radius: 0.75rem;
    border-left: 4px solid #4f46e5;
`;

const FeaturesTitle = styled.h4`
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    margin-top: 2rem;
    margin-bottom: 1rem;
`;

const FeaturesList = styled.ul`
    list-style: none;
    padding: 0;
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const FeatureItem = styled.li`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1rem;
    font-weight: 500;
    color: #374151;
    line-height: 1.5;
`;

const CheckIcon = styled.svg`
    width: 1.5rem;
    height: 1.5rem;
    flex: none;
    color: #4f46e5;
`;

const SelectButton = styled.a`
    margin-top: auto;
    display: block;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    text-align: center;
    font-size: 1rem;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    ${props => props.isEnterprise ? `
        background-color: #4f46e5;
        color: #ffffff;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        &:hover {
            background-color: #4338ca;
            transform: translateY(-2px);
        }
        &:focus-visible {
            outline: 2px solid #4f46e5;
            outline-offset: 2px;
        }
    ` : `
        color: #4f46e5;
        border: 2px solid #4f46e5;
        background: transparent;
        &:hover {
            background: #4f46e5;
            color: #ffffff;
        }
        &:focus-visible {
            outline: 2px solid #4f46e5;
            outline-offset: 2px;
        }
    `}
`;

const ProfessionalPricingPage = () => {
    const [currentPlan] = useState('1 Year Plan');
    const [accountStatus] = useState('active');
    const [balance] = useState(2500);
    const [plans, setPlans] = useState([
        {
            name: 'Hobby',
            price: 29,
            description: "The perfect plan if you're just getting started with our product.",
            features: [
                '25 products',
                'Up to 10,000 subscribers',
                'Advanced analytics',
                '24-hour support response time'
            ],
            buttonClass: 'btn-hobby'
        },
        {
            name: 'Enterprise',
            price: 99,
            description: 'Dedicated support and infrastructure for your company.',
            features: [
                'Unlimited products',
                'Unlimited subscribers',
                'Advanced analytics',
                'Dedicated support representative',
                'Marketing automations',
                'Custom integrations'
            ],
            buttonClass: 'btn-enterprise',
            isEnterprise: true
        }
    ]);

    useEffect(() => {
        list_plans()
            .then(response => {
                console.log("API raw response:", JSON.stringify(response, null, 2));
                const mappedPlans = response.data.map((plan, index) => {
                    const period = plan.freeAccessPeriod?.period || plan.billingDetails?.tiered?.intro?.[0]?.period?.period || 'N/A';
                    const periodUnit = plan.freeAccessPeriod?.periodUnit || plan.billingDetails?.tiered?.intro?.[0]?.period?.periodUnit || 'N/A';
                    const mappedPlan = {
                        name: plan.planName,
                        price: plan.billingDetails?.tiered?.intro?.[0]?.charges?.amount || plan.planPrice || 0,
                        description: plan.planDescription,
                        planType: plan.planType,
                        planDiscount: plan.planDiscount,
                        period: period,
                        periodUnit: periodUnit,
                        features: plan.access.modules.map(module => module.charAt(0).toUpperCase() + module.slice(1)),
                        buttonClass: plan.planType === 'FREE' ? 'btn-trial' : `btn-year${index % 2 + 1}`,
                        isEnterprise: index === 0
                    };
                    console.log(`Mapped plan ${index + 1}:`, JSON.stringify(mappedPlan, null, 2));
                    return mappedPlan;
                });
                console.log("Setting plans state:", JSON.stringify(mappedPlans, null, 2));
                setPlans(mappedPlans);
            })
            .catch(error => {
                console.error('Error fetching plans:', error);
                setPlans([]); // Fallback to empty array
            });
    }, []);

    const handleTopUp = () => {
        console.log('Top-up clicked');
    };

    const handlePlanSelect = (planName) => {
        console.log(`Selected plan: ${planName}`);
    };

    return (
        <div>
            
                  <div className="pageheader pd-t-25 pd-b-35 py-8">
        <div className="breadcrumb pd-0 mg-0">
          <a className="breadcrumb-item" href="index.html">
            <i className="icon ion-ios-home-outline" /> Home
          </a>
          <a className="breadcrumb-item active" href>
            Dashboard
          </a>
        </div>
      </div>
        <Section>
            <BackgroundGradient aria-hidden="true">
                <GradientShape />
            </BackgroundGradient>
            <Container>
                <PageHeader>
                    <PageTitle>Pricing</PageTitle>
                    <PageSubtitle>Choose the right plan for you</PageSubtitle>
                </PageHeader>
                <Description>
                    Choose an affordable plan that’s packed with the best features for engaging your audience, creating customer loyalty, and driving sales.
                </Description>
                <PricingGrid>
                    {plans.map((plan, index) => {
                        console.log(`Rendering plan ${index + 1}:`, {
                            name: plan.name,
                            period: plan.period,
                            periodUnit: plan.periodUnit
                        });
                        return (
                            <PricingCard key={index} isEnterprise={plan.isEnterprise}>
                                <PlanName id={`tier-${plan.name.toLowerCase()}`}>
                                    {plan.name}
                                </PlanName>
                                <PlanType planType={plan.planType}>{plan.planType}</PlanType>
                                <PlanPrice>
                                    <PriceAmount>Ksh {plan.price.toLocaleString()}</PriceAmount>
                                    <PricePeriod>
                                        /{plan.period} {plan.periodUnit && plan.periodUnit !== 'N/A' ? plan.periodUnit.toLowerCase() : ''}
                                    </PricePeriod>
                                </PlanPrice>
                                {plan.planDiscount > 0 && (
                                    <DiscountTag>{plan.planDiscount}% Discount</DiscountTag>
                                )}
                                <PlanDescription>{plan.description}</PlanDescription>
                                <FeaturesTitle>Features</FeaturesTitle>
                                <FeaturesList>
                                    {plan.features.map((feature, i) => (
                                        <FeatureItem key={i}>
                                            <CheckIcon viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" fillRule="evenodd" />
                                            </CheckIcon>
                                            <span>{feature}</span>
                                        </FeatureItem>
                                    ))}
                                </FeaturesList>
                                <SelectButton
                                    href="#"
                                    aria-describedby={`tier-${plan.name.toLowerCase()}`}
                                    isEnterprise={plan.isEnterprise}
                                    onClick={() => handlePlanSelect(plan.name)}
                                >
                                    Get started today
                                </SelectButton>
                            </PricingCard>
                        );
                    })}
                </PricingGrid>
            </Container>
        </Section>
        </div>

    );
};

export default ProfessionalPricingPage;